// Subscription screen of the account panel: a section per delivery day + time with
// a box per meal type. Quality has no backend for changes yet, so it's read-only.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { COLORS, FONTS } from 'styles/theme';
import { MEAL_TYPES, SUBSCRIPTION_BASE_PRICES, SUBSCRIPTION_DISCOUNT } from 'components/storefront/SubscriptionMemberPanel';
import SubscriptionMealCard, { subscriptionMealImage } from 'components/shared/SubscriptionMealCard';
import ProteinTier from 'components/shared/ProteinTier';
import DeliveryAddressFields from 'components/shared/DeliveryAddressFields';
import DeliverySlotSelect from 'components/shared/DeliverySlotSelect';
import ConfirmModal from 'components/storefront/ConfirmModal';
import { EditorActions, cardLinkStyle } from 'components/shared/MacroTargets';
import { formatDeliveryDate } from 'utils/deliveryDates';
import { groupAddOnsByCategory } from 'utils/menuSections';
import { preferredSlot, slotKey } from 'utils/deliverySlots';
import { PREMIUM_SURCHARGE } from 'constants/meals';

const { dark } = COLORS;
const MUTED = '#8a8d91';
const ORANGE = '#f9a826';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Thursday'];
const TIMES = ['Morning', 'Midday', 'Afternoon', 'Evening'];
const MAX_MEALS = 50;
const MEAL_ORDER = ['breakfast', 'lunch', 'dinner', 'snack'];
const isSnack = (mealType) => /snack/i.test(mealType || '');
const mealRank = (mealType) => {
  const i = MEAL_ORDER.indexOf(String(mealType || '').toLowerCase().replace(/s$/, ''));
  return i < 0 ? MEAL_ORDER.length : i;
};

const FIELD = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: '15px',
  border: '1px solid #d8d0bd', borderRadius: '8px', background: '#fff',
  color: dark, fontFamily: FONTS.sans,
};

const linkBtn = (color = dark) => ({
  background: 'none', border: 'none', padding: 0, cursor: 'pointer', color,
  fontSize: '12px', fontWeight: 700, textDecoration: 'underline', fontFamily: FONTS.sans,
});

const unitPrice = (mealType) => {
  const key = String(mealType || '').toLowerCase().replace(/s$/, '');
  const base = SUBSCRIPTION_BASE_PRICES[key];
  return base ? base * SUBSCRIPTION_DISCOUNT : 0;
};

const money = (n) => `$${n.toFixed(2)}`;
const sectionLabel = { fontSize: '11px', letterSpacing: '1px', color: MUTED, fontWeight: 700 };

const groupKeyOf = (s) => `${s.deliveryDay}|${s.deliveryTime}`;
// A delivery being added has no rows yet, so the editor stages everything in the draft.
const NEW_GROUP = { key: 'new', deliveryDay: '', deliveryTime: '', rows: [], addOns: [] };
const zip5 = (d) => String(d?.zip || '').trim().slice(0, 5);

const addressDraftOf = (s) => ({
  address: s.address.line1 || '', address2: s.address.line2 || '', city: s.address.city || '',
  state: s.address.state || '', zip: s.address.zip || '', phone: s.address.phone || '',
});

function Stepper({ value, onChange, min = 1, max = MAX_MEALS, label }) {
  const btn = (disabled) => ({
    width: '36px', height: '36px', borderRadius: '50%', border: '1px solid #d8d0bd',
    background: '#fff', color: dark, fontSize: '20px', lineHeight: 1, cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.35 : 1, fontFamily: FONTS.sans,
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '18px' }}>
      <button type="button" aria-label={`Fewer ${label}`} disabled={value <= min} onClick={() => onChange(value - 1)} style={btn(value <= min)}>−</button>
      <span style={{ minWidth: '32px', textAlign: 'center', fontSize: '22px', fontWeight: 700, color: dark }}>{value}</span>
      <button type="button" aria-label={`More ${label}`} disabled={value >= max} onClick={() => onChange(value + 1)} style={btn(value >= max)}>+</button>
    </div>
  );
}

// Horizontal row that scrolls by touch, mouse drag or the arrow buttons below it.
function ScrollRow({ children }) {
  const ref = useRef(null);
  const drag = useRef({ active: false, startX: 0, startLeft: 0, moved: false });
  const [dragging, setDragging] = useState(false);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 280, behavior: 'smooth' });

  // Touch scrolls natively; this adds click-and-drag for a mouse.
  const onPointerDown = (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    drag.current = { active: true, startX: e.clientX, startLeft: ref.current.scrollLeft, moved: false };
    setDragging(true);
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d.active) return;
    const dx = e.clientX - d.startX;
    if (Math.abs(dx) > 5) d.moved = true;
    ref.current.scrollLeft = d.startLeft - dx;
  };
  const endDrag = () => { drag.current.active = false; setDragging(false); };

  const navBtn = {
    flex: 1, height: '48px', borderRadius: '10px', border: '1px solid #d8d0bd', background: '#fff',
    color: dark, cursor: 'pointer', fontSize: '24px', lineHeight: 1, fontFamily: FONTS.sans,
  };

  return (
    <div style={{ minWidth: 0, maxWidth: '100%' }}>
      <div
        ref={ref}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onClickCapture={(e) => { if (drag.current.moved) { e.preventDefault(); e.stopPropagation(); drag.current.moved = false; } }}
        style={{
          display: 'flex', gap: '10px', overflowX: 'auto', padding: '2px 2px 8px', scrollbarWidth: 'none',
          scrollSnapType: dragging ? 'none' : 'x proximity', cursor: dragging ? 'grabbing' : 'grab',
          touchAction: 'pan-x pan-y', WebkitOverflowScrolling: 'touch', userSelect: dragging ? 'none' : 'auto',
        }}
      >
        {children}
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
        <button type="button" aria-label="Scroll left" onClick={() => scroll(-1)} style={navBtn}>‹</button>
        <button type="button" aria-label="Scroll right" onClick={() => scroll(1)} style={navBtn}>›</button>
      </div>
    </div>
  );
}

function AddOnRow({ items, busy, onAdd }) {
  return (
    <ScrollRow>
        {items.map((dish) => (
          <div key={dish.id} style={{
            flex: '0 0 132px', scrollSnapAlign: 'start', border: '1px solid #ececec', borderRadius: '10px',
            overflow: 'hidden', background: '#fff', display: 'flex', flexDirection: 'column',
          }}>
            <div style={{
              height: '84px', background: dish.imageUrl ? `center / cover no-repeat url(${dish.imageUrl})` : COLORS.cream,
            }} />
            <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
              <div style={{
                fontSize: '12px', lineHeight: 1.3, color: dark, display: '-webkit-box', WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>{dish.name}</div>
              <div style={{ fontSize: '12px', color: MUTED, marginTop: 'auto' }}>{money(dish.price)}</div>
              <button type="button" disabled={busy} onClick={() => onAdd(dish)} style={{
                padding: '5px 0', border: `1px solid ${ORANGE}`, borderRadius: '6px', background: '#fff4dc', color: dark,
                fontSize: '12px', fontWeight: 700, fontFamily: FONTS.sans, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
              }}>Add</button>
            </div>
          </div>
        ))}
    </ScrollRow>
  );
}

function LineItem({ name, detail, each, eachUnit = 'each', note, total }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', color: dark }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: 700 }}>{name}</div>
        <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>
          {detail}{each > 0 ? ` · ${money(each)} ${eachUnit}` : ''}
        </div>
        {note && <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>{note}</div>}
      </div>
      {total > 0 && <div style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>{money(total)}</div>}
    </div>
  );
}


const tailoredName = (mealType) => {
  if (isSnack(mealType)) return 'Tailored Snacks';
  return /^(breakfast|lunch|dinner)$/i.test(mealType || '') ? `Tailored ${mealType}` : (mealType || 'Meal Plan');
};
// Snack rows count days of snacks per week, priced per day.
const countUnit = (mealType) => (isSnack(mealType) ? 'days / week' : 'meals / week');

const mealSummary = (g, mealsOf, draft = null) => {
  const counts = [
    ...g.rows.map((s) => [s.mealType, mealsOf(s)]),
    ...Object.entries(draft?.newMeals || {}),
  ].filter(([, n]) => n > 0).sort(([a], [b]) => mealRank(a) - mealRank(b));
  const parts = counts.map(([mealType, n]) => (isSnack(mealType) ? `${n} days of snacks` : `${n} ${(mealType || 'meal').toLowerCase()}`));
  const addOnCount = g.addOns.reduce((sum, a) => sum + mealsOf(a), 0)
    + Object.values(draft?.added || {}).reduce((sum, a) => sum + a.qty, 0);
  if (addOnCount) parts.push(`${addOnCount} add-on${addOnCount === 1 ? '' : 's'}`);
  return parts.join(', ');
};

export default function SubscriptionScreen({ subscriptions, onSave, onCancel, onSkipDeliveries, onAddDelivery, onLoadAddOns, onAddAddOn, onAddMeal, onLoadDeliveryOptions, onSignup, mealTargets = [], defaultMealTargets = {}, mealPlanUrl = '/meal-plan' }) {
  const [expanded, setExpanded] = useState({});      // groupKey -> true when open
  // One delivery edited at a time, like the allergy editor: { key, deliveryDay, deliveryTime, ...address, meals, removed }
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [confirmSave, setConfirmSave] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(null); // groupKey
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [catalog, setCatalog] = useState(null);
  const [slotsByZip, setSlotsByZip] = useState({});    // zip -> [{ day, time, fee }], null if the lookup failed

  const groups = useMemo(() => {
    const map = new Map();
    subscriptions.forEach((s) => {
      const key = groupKeyOf(s);
      if (!map.has(key)) map.set(key, { key, deliveryDay: s.deliveryDay, deliveryTime: s.deliveryTime, rows: [], addOns: [] });
      (s.isAddOn ? map.get(key).addOns : map.get(key).rows).push(s);
    });
    const dayIdx = (d) => { const i = DAYS.indexOf(d); return i < 0 ? 99 : i; };
    const timeIdx = (t) => { const i = TIMES.indexOf(t); return i < 0 ? 99 : i; };
    map.forEach((g) => g.rows.sort((a, b) => mealRank(a.mealType) - mealRank(b.mealType)));
    return [...map.values()].sort((a, b) =>
      dayIdx(a.deliveryDay) - dayIdx(b.deliveryDay) || timeIdx(a.deliveryTime) - timeIdx(b.deliveryTime));
  }, [subscriptions]);

  const isNewDraft = draft?.key === NEW_GROUP.key;
  const editingGroup = isNewDraft ? NEW_GROUP : groups.find((g) => g.key === draft?.key) || null;
  const mealsOf = (s) => (draft?.removed[s.recordId] ? 0 : draft?.meals[s.recordId] ?? s.mealsIncluded);
  const targetFor = (mealType) => {
    const key = (mealType || '').toLowerCase().replace(/s$/, '');
    return mealTargets.find((m) => (m.meal || '').toLowerCase().replace(/s$/, '') === key);
  };
  // A meal the quiz skipped reads 0 until it's added, then the default targets it's portioned with.
  const nutritionFor = (mealType, qty) => {
    const target = targetFor(mealType);
    if (!target?.skipped) return target?.calories > 0 ? target : null;
    if (qty === 0) return { calories: 0, carbsG: 0, proteinG: 0, fatG: 0, fiberG: 0 };
    const key = Object.keys(defaultMealTargets).find((m) => mealRank(m) === mealRank(mealType) && mealRank(m) < MEAL_ORDER.length);
    return key ? defaultMealTargets[key] : null;
  };
  const premiumOf = (s) => draft?.premium[s.recordId] ?? s.premium;
  // Same rule as the meal plan's refunds: the premium surcharge is per meal, not on snacks.
  const mealPrice = (s) => unitPrice(s.mealType) + (premiumOf(s) && !isSnack(s.mealType) ? PREMIUM_SURCHARGE : 0);
  // A meal type not on this delivery yet takes its tier from the same meal on another day, since they share a Client row.
  const knownPremium = (mealType) => (subscriptions || []).some((s) => !s.isAddOn && s.premium && mealRank(s.mealType) === mealRank(mealType));
  const newPremiumOf = (mealType) => draft?.newPremium[mealType] ?? knownPremium(mealType);
  const newMealPrice = (mealType) => unitPrice(mealType) + (newPremiumOf(mealType) && !isSnack(mealType) ? PREMIUM_SURCHARGE : 0);
  const mealSlots = (g) => {
    const slots = MEAL_TYPES.map((t) => ({ key: t.key, mealType: t.mealType, row: g.rows.find((r) => mealRank(r.mealType) === mealRank(t.mealType)) || null }));
    g.rows.filter((r) => !slots.some((x) => x.row === r)).forEach((r) => slots.push({ key: r.recordId, mealType: r.mealType, row: r }));
    return slots;
  };
  const groupTotal = (g) => g.rows.reduce((sum, s) => sum + mealsOf(s) * mealPrice(s), 0)
    + (draft?.key === g.key ? Object.entries(draft.newMeals).reduce((sum, [mealType, n]) => sum + n * newMealPrice(mealType), 0) : 0)
    + g.addOns.reduce((sum, s) => sum + mealsOf(s) * (s.dishPrice || 0), 0)
    + (draft?.key === g.key ? Object.values(draft.added).reduce((sum, a) => sum + a.qty * (a.dish.price || 0), 0) : 0);
  const draftSlotFee = () => slotsByZip[zip5(draft)]?.find((s) => slotKey(s.day, s.time) === slotKey(draft.deliveryDay, draft.deliveryTime))?.fee || 0;
  const feeOf = (g) => (g === NEW_GROUP ? draftSlotFee() : (g.rows[0] || g.addOns[0]).deliveryFee || 0);
  const total = (isNewDraft ? [...groups, NEW_GROUP] : groups).reduce((sum, g) => sum + groupTotal(g) + feeOf(g), 0);
  const ownedSlots = new Set(groups.map((g) => g.key));

  // Other days' slots are left out so an edit can't merge two deliveries.
  const offeredSlots = (g, zip) => slotsByZip[zip]?.filter((s) => {
    const key = `${s.day}|${s.time}`;
    return key === g.key || !ownedSlots.has(key);
  });
  const slotOffered = (g, d) => {
    const offered = offeredSlots(g, zip5(d));
    return !offered || offered.some((s) => slotKey(s.day, s.time) === slotKey(d.deliveryDay, d.deliveryTime));
  };
  const draftCount = !!draft && !!editingGroup && ([...editingGroup.rows, ...editingGroup.addOns].reduce((sum, s) => sum + mealsOf(s), 0)
    + Object.values(draft.newMeals).reduce((sum, n) => sum + n, 0) + Object.values(draft.added).reduce((sum, a) => sum + a.qty, 0));
  const draftInvalid = !!draft && !!editingGroup
    && ((draft.address && !(draft.city && draft.state && draft.zip)) || !slotOffered(editingGroup, draft) || draftCount === 0
      || (isNewDraft && !(draft.address && offeredSlots(NEW_GROUP, zip5(draft))?.length)));

  const editingZip = zip5(draft);
  useEffect(() => {
    if (!/^\d{5}$/.test(editingZip) || editingZip in slotsByZip) return undefined;
    let cancelled = false;
    onLoadDeliveryOptions(editingZip)
      .then((days) => {
        if (cancelled) return;
        const slots = days.flatMap((d) => d.slots.map((o) => ({ day: d.day, time: o.time, fee: o.cost })));
        setSlotsByZip((prev) => ({ ...prev, [editingZip]: slots }));
      })
      .catch((e) => {
        if (cancelled) return;
        setSlotsByZip((prev) => ({ ...prev, [editingZip]: null }));
        setEditError(e.message || 'Could not load delivery times.');
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingZip]);

  // Like checkout: when the address's slots arrive, a slot it doesn't offer falls back to the preferred one.
  useEffect(() => {
    const offered = editingGroup ? offeredSlots(editingGroup, editingZip) : null;
    if (!offered?.length || slotOffered(editingGroup, draft)) return;
    const p = preferredSlot(offered);
    patchDraft({ deliveryDay: p.day, deliveryTime: p.time });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.key, editingZip, slotsByZip]);

  const patchDraft = (patch) => setDraft((prev) => (prev ? { ...prev, ...patch } : prev));

  const startEdit = (g) => {
    setDraft({
      key: g.key, deliveryDay: g.deliveryDay, deliveryTime: g.deliveryTime,
      ...addressDraftOf(g.rows[0] || g.addOns[0]), meals: {}, removed: {}, premium: {}, added: {}, newMeals: {}, newPremium: {},
    });
    setExpanded((prev) => ({ ...prev, [g.key]: true }));
    setEditError('');
    setConfirmCancel(null);
  };

  // Starts at zero meals like the subscription builder; only the address is prefilled.
  const startNew = () => {
    const first = groups[0].rows[0] || groups[0].addOns[0];
    setDraft({
      key: NEW_GROUP.key, step: 'address', deliveryDay: '', deliveryTime: '',
      ...addressDraftOf(first), meals: {}, removed: {}, premium: {}, added: {}, newMeals: {}, newPremium: {},
    });
    setEditError('');
    setError('');
    setConfirmCancel(null);
  };

  const cancelEdit = () => { setDraft(null); setEditError(''); };

  const saveNewDelivery = async () => {
    setSaving(true);
    setEditError('');
    try {
      await onAddDelivery({
        deliveryDay: draft.deliveryDay, deliveryTime: draft.deliveryTime,
        address: draft.address, address2: draft.address2, city: draft.city,
        state: draft.state, zip: draft.zip, phone: draft.phone,
        meals: Object.entries(draft.newMeals).filter(([, n]) => n > 0).map(([mealType, quantity]) => ({
          mealType, quantity, ...(mealType in draft.newPremium ? { premium: draft.newPremium[mealType] } : {}),
        })),
        addOns: Object.entries(draft.added).map(([dishRecordId, { qty }]) => ({ dishRecordId, quantity: qty })),
      });
      setExpanded((prev) => ({ ...prev, [`${draft.deliveryDay}|${draft.deliveryTime}`]: true }));
      setDraft(null);
    } catch (e) {
      setEditError(e.message || 'Could not add that delivery.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (isNewDraft) { await saveNewDelivery(); return; }
    const g = editingGroup;
    const original = { deliveryDay: g.deliveryDay, deliveryTime: g.deliveryTime, ...addressDraftOf(g.rows[0] || g.addOns[0]) };
    const delivery = Object.fromEntries(Object.keys(original).map((k) => [k, draft[k] || '']));
    const deliveryChanged = Object.keys(original).some((k) => String(original[k]) !== String(delivery[k]));
    const all = [...g.rows, ...g.addOns];
    // Rows set to 0 or removed are deleted last, so until then any row can serve as the adds' template.
    const dropped = all.filter((s) => draft.removed[s.recordId] || mealsOf(s) === 0);
    const template = all.find((s) => !dropped.includes(s)) || all[0];
    // Rows save one at a time; on a failure the draft stays open so Save can be retried.
    const forget = (field, key) => setDraft((prev) => {
      const next = { ...prev[field] };
      delete next[key];
      return { ...prev, [field]: next };
    });
    setSaving(true);
    setEditError('');
    try {
      for (const s of all) {
        if (draft.removed[s.recordId]) continue;
        const patch = deliveryChanged ? { ...delivery } : {};
        const premium = draft.premium[s.recordId];
        if (premium != null && premium !== s.premium) patch.premium = premium;
        const n = draft.meals[s.recordId];
        if (n > 0 && n !== s.mealsIncluded) patch.mealsIncluded = n;
        // eslint-disable-next-line no-await-in-loop
        if (Object.keys(patch).length) await onSave(s.recordId, patch);
      }
      // Added after the patches, so the new rows copy the template's already-saved day, time and address.
      for (const [mealType, n] of Object.entries(draft.newMeals)) {
        if (n > 0) {
          const premium = draft.newPremium[mealType];
          // eslint-disable-next-line no-await-in-loop
          await onAddMeal(template.recordId, mealType, n, premium);
        }
        forget('newMeals', mealType);
      }
      for (const [dishId, { qty }] of Object.entries(draft.added)) {
        // eslint-disable-next-line no-await-in-loop
        await onAddAddOn(template.recordId, dishId, qty);
        forget('added', dishId);
      }
      for (const s of dropped) {
        // eslint-disable-next-line no-await-in-loop
        await onCancel(s.recordId);
      }
      setExpanded((prev) => ({ ...prev, [`${delivery.deliveryDay}|${delivery.deliveryTime}`]: true }));
      setDraft(null);
    } catch (e) {
      setEditError(e.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    onLoadAddOns().then((items) => { if (!cancelled) setCatalog(items); })
      .catch((e) => { if (!cancelled) setError(e.message || 'Could not load add-ons.'); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // An add-on already on this delivery gets one more; a new one waits in the draft until Save.
  const stageAddOn = (g, dish) => {
    const existing = g.addOns.find((a) => a.presetDishId === dish.id);
    if (existing) {
      patchDraft({
        meals: { ...draft.meals, [existing.recordId]: draft.removed[existing.recordId] ? 1 : Math.min(MAX_MEALS, mealsOf(existing) + 1) },
        removed: { ...draft.removed, [existing.recordId]: false },
      });
      return;
    }
    const qty = Math.min(MAX_MEALS, (draft.added[dish.id]?.qty || 0) + 1);
    patchDraft({ added: { ...draft.added, [dish.id]: { dish, qty } } });
  };
  const setStagedQty = (dishId, qty) => {
    const added = { ...draft.added };
    if (qty > 0) added[dishId] = { ...added[dishId], qty }; else delete added[dishId];
    patchDraft({ added });
  };

  const handleCancelGroup = async (g) => {
    setBusy(true);
    setError('');
    try {
      for (const s of [...g.rows, ...g.addOns]) {
        // eslint-disable-next-line no-await-in-loop
        await onCancel(s.recordId);
      }
      setConfirmCancel(null);
    } catch (e) {
      setError(e.message || 'Could not cancel your subscription.');
    } finally {
      setBusy(false);
    }
  };

  if (!groups.length) {
    return (
      <div style={{ textAlign: 'center', padding: '36px 16px', fontFamily: FONTS.sans }}>
        <p style={{ color: MUTED, fontSize: '14px', margin: '0 0 14px' }}>You don't have an active subscription yet.</p>
        <button type="button" onClick={onSignup} style={{
          padding: '12px 20px', border: 'none', borderRadius: '8px', background: ORANGE,
          color: '#fff', fontWeight: 700, fontSize: '15px', fontFamily: FONTS.sans, cursor: 'pointer',
        }}>Sign up for a subscription</button>
      </div>
    );
  }

  const slotPicker = (g) => {
    const zip = zip5(draft);
    const offered = offeredSlots(g, zip);
    if (!/^\d{5}$/.test(zip)) return <span style={{ fontSize: '13px', color: MUTED }}>Pick an address to see delivery times.</span>;
    if (offered === undefined) return <span style={{ fontSize: '13px', color: MUTED }}>Checking delivery times…</span>;
    if (offered === null) return <span style={{ fontSize: '13px', color: MUTED }}>Could not load delivery times.</span>;
    if (!offered.length) {
      return slotsByZip[zip].length
        ? <span style={{ fontSize: '13px', color: '#c0392b' }}>You already have every delivery time offered for this address.</span>
        : <span style={{ fontSize: '13px', color: '#c0392b' }}>We don&apos;t deliver to that zip code yet.</span>;
    }
    return (
      <DeliverySlotSelect
        slots={offered}
        value={slotKey(draft.deliveryDay, draft.deliveryTime)}
        onChange={(slot) => { if (slot) patchDraft({ deliveryDay: slot.day, deliveryTime: slot.time }); }}
        showFee
        style={FIELD}
      />
    );
  };

  const addressFields = (g) => (
    <>
      <div style={sectionLabel}>DELIVERY ADDRESS</div>
      <div style={{ display: 'grid', gap: '12px' }}>
        <DeliveryAddressFields
          value={draft}
          onChange={patchDraft}
          idPrefix={g === NEW_GROUP ? 'subscription-new' : `subscription-${slotKey(g.deliveryDay, g.deliveryTime).replace('|', '-')}`}
          showNotes={false}
        />
      </div>
    </>
  );

  const slotField = (g) => (
    <label style={{ display: 'block' }}>
      <span style={{ ...sectionLabel, display: 'block', margin: '4px 0 6px' }}>DELIVERY DAY &amp; TIME</span>
      {slotPicker(g)}
    </label>
  );

  const NEW_STEPS = ['address', 'slot', 'meals'];
  const stepNav = (step) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span style={sectionLabel}>STEP {NEW_STEPS.indexOf(step) + 1} OF {NEW_STEPS.length}</span>
      {step !== 'address' && (
        <button type="button" disabled={saving} onClick={() => patchDraft({ step: NEW_STEPS[NEW_STEPS.indexOf(step) - 1] })} style={linkBtn(MUTED)}>Back</button>
      )}
    </div>
  );

  // The new delivery's address and slot each get a step before the meals.
  const newDeliveryStep = (step) => {
    const ready = step === 'address'
      ? !!(draft.address && draft.city && draft.state && /^\d{5}$/.test(zip5(draft)))
      : !!(draft.deliveryDay && offeredSlots(NEW_GROUP, zip5(draft))?.length && slotOffered(NEW_GROUP, draft));
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '10px' }}>
        {stepNav(step)}
        {step === 'address' ? addressFields(NEW_GROUP) : slotField(NEW_GROUP)}
        {editError && <p style={{ color: '#c0392b', fontSize: '13px', margin: 0 }} role="alert">{editError}</p>}
        <EditorActions
          saving={false}
          disabled={!ready}
          onSave={() => patchDraft({ step: NEW_STEPS[NEW_STEPS.indexOf(step) + 1] })}
          onCancel={cancelEdit}
          saveLabel="Continue"
        />
      </div>
    );
  };

  const editor = (g) => {
    const step = g === NEW_GROUP ? draft.step : null;
    if (step === 'address' || step === 'slot') return newDeliveryStep(step);
    return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '10px' }}>
      {step ? stepNav(step) : (
        <>
          {addressFields(g)}
          {slotField(g)}
        </>
      )}

      <div style={{ ...sectionLabel, marginTop: '6px' }}>MEALS</div>
      <ScrollRow>
      {mealSlots(g).map(({ key, mealType, row }) => {
        const snack = isSnack(mealType);
        const target = targetFor(mealType);
        const premium = row ? premiumOf(row) : newPremiumOf(mealType);
        const price = row ? mealPrice(row) : newMealPrice(mealType);
        const qty = row ? mealsOf(row) : draft.newMeals[mealType] || 0;
        const setQty = (n) => (row
          ? patchDraft({ meals: { ...draft.meals, [row.recordId]: n } })
          : patchDraft({ newMeals: { ...draft.newMeals, [mealType]: n } }));
        const setPremium = (p) => (row
          ? patchDraft({ premium: { ...draft.premium, [row.recordId]: p } })
          : patchDraft({ newPremium: { ...draft.newPremium, [mealType]: p } }));
        // One Client row per person + meal type backs every delivery day of that meal type.
        const sharesTier = groups.some((o) => o.key !== g.key && o.rows.some((r) => mealRank(r.mealType) === mealRank(mealType)));
        return (
          <div key={key} style={{ flex: '0 0 240px', scrollSnapAlign: 'start', display: 'grid' }}>
          <SubscriptionMealCard
            label={tailoredName(mealType)}
            image={MEAL_TYPES.some((t) => t.key === key) ? subscriptionMealImage(key) : null}
            price={price}
            unit={`${snack ? '/ day' : 'ea'} · 10% off`}
            note={snack && target?.snacksPerDay > 0 ? `${target.snacksPerDay} snack${target.snacksPerDay > 1 ? 's' : ''} per day` : null}
            nutrition={nutritionFor(mealType, qty)}
            qty={qty}
            max={MAX_MEALS}
            onChange={setQty}
          >
            {!snack && (
              <div>
                <ProteinTier premium={premium} onChange={setPremium} />
                {sharesTier && (
                  <p style={{ fontSize: '12px', color: MUTED, margin: '6px 0 0', lineHeight: 1.6 }}>
                    Also changes {mealType.toLowerCase()} on your other delivery days.
                  </p>
                )}
              </div>
            )}
          </SubscriptionMealCard>
          </div>
        );
      })}
      </ScrollRow>
      {draftCount === 0 && (g === NEW_GROUP
        ? <p style={{ fontSize: '12px', color: MUTED, margin: 0 }}>Add at least one meal or add-on.</p>
        : <p style={{ fontSize: '12px', color: '#c0392b', margin: 0 }}>Keep at least one meal or add-on, or use &ldquo;Cancel this delivery&rdquo; instead.</p>
      )}

      <div style={{ ...sectionLabel, marginTop: '6px' }}>ADD-ONS</div>
      {g.addOns.filter((a) => !draft.removed[a.recordId]).map((a) => (
        <div key={a.recordId} style={{ padding: '12px 14px', borderRadius: '10px', border: '1px solid #d8d0bd', background: '#fff', fontSize: '13px', color: dark }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
            <span>{a.dishName || 'Add-on'}</span>
            {a.dishPrice > 0 && <span style={{ color: MUTED }}>{money(mealsOf(a) * a.dishPrice)} / week</span>}
          </div>
          <div style={{ marginTop: '6px' }}>
            <Stepper
              value={mealsOf(a)}
              label={a.dishName || 'add-on'}
              onChange={(n) => patchDraft({ meals: { ...draft.meals, [a.recordId]: n } })}
            />
          </div>
          <div style={{ textAlign: 'center', marginTop: '4px' }}>
            <button type="button" onClick={() => patchDraft({ removed: { ...draft.removed, [a.recordId]: true } })} style={linkBtn(MUTED)}>Remove</button>
          </div>
        </div>
      ))}
      {Object.entries(draft.added).map(([dishId, { dish, qty }]) => (
        <div key={dishId} style={{ padding: '12px 14px', borderRadius: '10px', border: '1px solid #d8d0bd', background: '#fff', fontSize: '13px', color: dark }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
            <span>{dish.name}</span>
            {dish.price > 0 && <span style={{ color: MUTED }}>{money(qty * dish.price)} / week</span>}
          </div>
          <div style={{ marginTop: '6px' }}>
            <Stepper value={qty} label={dish.name} onChange={(n) => setStagedQty(dishId, n)} />
          </div>
          <div style={{ textAlign: 'center', marginTop: '4px' }}>
            <button type="button" onClick={() => setStagedQty(dishId, 0)} style={linkBtn(MUTED)}>Remove</button>
          </div>
        </div>
      ))}
      <div style={{ padding: '12px 14px', borderRadius: '10px', border: '1px solid #d8d0bd', background: '#fff', fontSize: '13px', color: dark, minWidth: 0, overflow: 'hidden' }}>
        <div style={{ fontWeight: 700 }}>Add an add-on</div>
        <div style={{ fontSize: '12px', color: MUTED, margin: '4px 0 6px' }}>Added weekly to this delivery and charged with your subscription.</div>
        {!catalog && <div style={{ color: MUTED }}>Loading…</div>}
        {catalog && catalog.length === 0 && <div style={{ color: MUTED }}>No add-ons available right now.</div>}
        {catalog && catalog.length > 0 && groupAddOnsByCategory(catalog, (d) => d.categories).map((group, _, all) => (
          <div key={group.id} style={{ marginTop: '8px' }}>
            {all.length > 1 && <div style={{ fontSize: '13px', fontWeight: 700, color: dark, marginBottom: '4px' }}>{group.label}</div>}
            <AddOnRow items={group.items} busy={saving} onAdd={(dish) => stageAddOn(g, dish)} />
          </div>
        ))}
      </div>

      <p style={{ fontSize: '12px', color: MUTED, margin: '4px 0 0', lineHeight: 1.6 }}>
        {g === NEW_GROUP ? 'Starts with your next weekly charge.' : 'Changes apply from your next weekly charge.'}
      </p>
      {editError && <p style={{ color: '#c0392b', fontSize: '13px', margin: 0 }} role="alert">{editError}</p>}
      <EditorActions saving={saving} disabled={draftInvalid} onSave={() => setConfirmSave(true)} onCancel={cancelEdit} saveLabel={g === NEW_GROUP ? 'Add Delivery' : 'Save Delivery'} />
      <ConfirmModal
        open={confirmSave}
        tone="warn"
        icon="!"
        title={g === NEW_GROUP ? 'Starts next cycle' : 'Changes start next cycle'}
        confirmLabel={g === NEW_GROUP ? 'Add delivery' : 'Save changes'}
        confirmClassName="sf-btn-flow--primary"
        onConfirm={() => { setConfirmSave(false); handleSave(); }}
        onCancel={() => setConfirmSave(false)}
      >
        These changes apply from your next weekly cycle. Orders already placed, including this week&apos;s
        deliveries, stay as they are. To change this week&apos;s meals, please go to your{' '}
        <a href={mealPlanUrl} style={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline' }}>meal plan</a>.
      </ConfirmModal>
    </div>
    );
  };

  const readOnlyView = (g) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '8px' }}>
      {!draft && (
        <div style={{ textAlign: 'right' }}>
          <button type="button" onClick={() => startEdit(g)} style={{ ...cardLinkStyle, marginTop: 0, color: COLORS.orange }}>Edit</button>
        </div>
      )}
      {g.rows.filter((s) => mealsOf(s) > 0).map((s) => (
        <LineItem
          key={s.recordId}
          name={`${tailoredName(s.mealType)}${isSnack(s.mealType) && targetFor(s.mealType)?.snacksPerDay > 0 ? ` (${targetFor(s.mealType).snacksPerDay} per day)` : ''}`}
          detail={`${isSnack(s.mealType) ? '' : `${premiumOf(s) ? 'Premium' : 'Standard'} protein · `}${mealsOf(s)} ${countUnit(s.mealType)}`}
          each={mealPrice(s)}
          eachUnit={isSnack(s.mealType) ? '/ day' : 'each'}
          note={nutritionFor(s.mealType, mealsOf(s))?.calories > 0 ? `${Math.round(nutritionFor(s.mealType, mealsOf(s)).calories)} kcals / ${isSnack(s.mealType) ? 'snack' : 'meal'}` : null}
          total={mealsOf(s) * mealPrice(s)}
        />
      ))}
      {g.addOns.map((a) => (
        <LineItem
          key={a.recordId}
          name={a.dishName || 'Add-on'}
          detail={`${mealsOf(a)} / week`}
          each={a.dishPrice || 0}
          total={mealsOf(a) * (a.dishPrice || 0)}
        />
      ))}
    </div>
  );

  return (
    <div style={{ fontFamily: FONTS.sans, display: 'flex', flexDirection: 'column', minHeight: '100%', minWidth: 0, maxWidth: '100%' }}>
      <div style={{ padding: '16px', borderBottom: '1px solid #ececec', background: COLORS.cream }}>
        <div style={{ fontSize: '11px', letterSpacing: '1px', color: MUTED }}>ESTIMATED WEEKLY TOTAL</div>
        <div style={{ fontSize: '28px', fontWeight: 700, color: dark, fontFamily: FONTS.serif }}>{money(total)}</div>
      </div>

      <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '12px', flex: 1, alignContent: 'start' }}>
        {groups.map((g) => {
          const editing = draft?.key === g.key;
          const open = editing || !!expanded[g.key];
          const day = editing ? draft.deliveryDay : g.deliveryDay;
          const time = editing ? draft.deliveryTime : g.deliveryTime;
          const first = g.rows[0] || g.addOns[0];
          const addr = first.address;
          const paused = first.isPaused;
          return (
            <section key={g.key} style={{ border: `1px solid ${COLORS.creamAlt}`, borderRadius: '12px', background: '#fff', minWidth: 0 }}>
              <button
                type="button"
                aria-expanded={open}
                disabled={editing}
                onClick={() => setExpanded((prev) => ({ ...prev, [g.key]: !prev[g.key] }))}
                style={{
                  width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '14px',
                  cursor: editing ? 'default' : 'pointer', fontFamily: FONTS.sans, color: dark,
                  display: 'flex', gap: '12px', alignItems: 'flex-start',
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontFamily: FONTS.serif, fontSize: '20px', fontWeight: 700 }}>
                    {day} · {time}
                    {paused && <span style={{ fontFamily: FONTS.sans, fontSize: '11px', fontWeight: 700, color: ORANGE, marginLeft: '8px', letterSpacing: '0.5px' }}>SKIPPING</span>}
                  </span>
                  <span style={{ display: 'block', fontSize: '13px', marginTop: '4px' }}>{mealSummary(g, mealsOf, editing ? draft : null)}</span>                  <span style={{ display: 'block', fontSize: '13px', color: MUTED, marginTop: '2px' }}>
                    {money(groupTotal(g))} / week{feeOf(g) > 0 ? ` + ${money(feeOf(g))} delivery` : ''}
                  </span>
                  <span style={{ display: 'block', fontSize: '13px', color: MUTED, marginTop: '2px' }}>
                    {addr.line1 ? [addr.line1, [addr.city, addr.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ') : 'No delivery address saved'}
                  </span>
                </span>
                {!editing && <span aria-hidden="true" style={{ fontSize: '14px', color: MUTED, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>}
              </button>

              {open && (
                <div style={{ padding: '0 14px 14px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '12px', minWidth: 0 }}>
                  {paused && (
                    <div style={{ background: '#fff4dc', border: '1px solid #f3d9a4', borderRadius: '10px', padding: '10px 12px', fontSize: '13px', color: dark }}>
                      Skipped deliveries — next delivery {formatDeliveryDate(first.nextDeliveryDate)}.{' '}
                      <button type="button" disabled={!!draft} onClick={onSkipDeliveries} style={linkBtn()}>Manage skips</button>
                    </div>
                  )}

                  {editing ? editor(g) : readOnlyView(g)}

                  {!editing && (
                    <>
                      <div style={{ textAlign: 'right' }}>
                        <button type="button" onClick={onSkipDeliveries} style={{ ...linkBtn(dark), fontSize: '14px' }}>Skip deliveries</button>
                      </div>

                      <div style={{ textAlign: 'right' }}>
                        {confirmCancel === g.key ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '14px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '12px', color: dark }}>Cancel all {g.rows.length} meal plan{g.rows.length === 1 ? '' : 's'} for {g.deliveryDay} · {g.deliveryTime}?</span>
                            <button type="button" disabled={busy} onClick={() => handleCancelGroup(g)} style={linkBtn('#c0392b')}>
                              {busy ? 'Cancelling…' : 'Yes, cancel'}
                            </button>
                            <button type="button" onClick={() => setConfirmCancel(null)} style={linkBtn(MUTED)}>Never mind</button>
                          </div>
                        ) : (
                          <button type="button" onClick={() => setConfirmCancel(g.key)} style={linkBtn(MUTED)}>Cancel this plan</button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </section>
          );
        })}

        {error && <p style={{ color: '#c0392b', fontSize: '13px', margin: 0 }} role="alert">{error}</p>}

        {isNewDraft ? (
          <section style={{ border: `1px solid ${COLORS.creamAlt}`, borderRadius: '12px', background: '#fff', minWidth: 0 }}>
            <div style={{ padding: '14px', color: dark }}>
              <span style={{ display: 'block', fontFamily: FONTS.serif, fontSize: '20px', fontWeight: 700 }}>
                {draft.step === 'meals' ? `${draft.deliveryDay} · ${draft.deliveryTime}` : 'New delivery'}
              </span>
              {draftCount > 0 && <span style={{ display: 'block', fontSize: '13px', marginTop: '4px' }}>{mealSummary(NEW_GROUP, mealsOf, draft)}</span>}
              {draft.step === 'meals' && (
                <span style={{ display: 'block', fontSize: '13px', color: MUTED, marginTop: '2px' }}>
                  {money(groupTotal(NEW_GROUP))} / week{feeOf(NEW_GROUP) > 0 ? ` + ${money(feeOf(NEW_GROUP))} delivery` : ''}
                </span>
              )}
              {draft.step !== 'address' && (
                <span style={{ display: 'block', fontSize: '13px', color: MUTED, marginTop: '2px' }}>
                  {[draft.address, [draft.city, draft.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                </span>
              )}
            </div>
            <div style={{ padding: '0 14px 14px', minWidth: 0 }}>{editor(NEW_GROUP)}</div>
          </section>
        ) : (
          <button type="button" onClick={startNew} disabled={!!draft} style={{
            padding: '14px', borderRadius: '12px', border: `2px dashed ${COLORS.orange}`, background: 'transparent',
            color: COLORS.orange, fontFamily: FONTS.sans, fontSize: '15px', fontWeight: 700, cursor: draft ? 'default' : 'pointer', opacity: draft ? 0.5 : 1,
          }}>
            <div style={{ fontSize: '28px', lineHeight: 1 }}>+</div>
            Add delivery day
          </button>
        )}
      </div>
    </div>
  );
}
